<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BoardsAPI from 'kanban/api/boards.js';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', null);

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  const dynamic = flowDynamicVariables?.value ?? [];
  return [...base, ...dynamic];
});

// ─── Board / Step ─────────────────────────────────────────────────────────────

const boards = ref([]);
const steps = ref([]);
const loadingSteps = ref(false);

const boardOptions = computed(() =>
  boards.value.map(b => ({ id: b.id, name: b.name }))
);
const stepOptions = computed(() =>
  steps.value.map(s => ({ id: s.id, name: s.name }))
);
const selectedBoard = computed(
  () =>
    boardOptions.value.find(b => b.id === props.modelValue?.board_id) ?? null
);
const selectedStep = computed(
  () => stepOptions.value.find(s => s.id === props.modelValue?.step_id) ?? null
);
const PRIORITY_OPTIONS = computed(() => [
  { id: 'urgent', name: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.PRIORITY_URGENT') },
  { id: 'high', name: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.PRIORITY_HIGH') },
  { id: 'medium', name: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.PRIORITY_MEDIUM') },
  { id: 'low', name: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.PRIORITY_LOW') },
]);

const selectedPriority = computed(
  () =>
    PRIORITY_OPTIONS.value.find(p => p.id === props.modelValue?.priority) ?? null
);

async function loadBoards() {
  try {
    const { data } = await BoardsAPI.get();
    boards.value = data?.boards ?? [];
  } catch {
    boards.value = [];
  }
}

async function loadSteps(boardId) {
  if (!boardId) {
    steps.value = [];
    return;
  }
  loadingSteps.value = true;
  try {
    const { data } = await BoardsAPI.getOne(boardId);
    steps.value = data?.steps ?? [];
  } catch {
    steps.value = [];
  } finally {
    loadingSteps.value = false;
  }
}

watch(
  () => props.modelValue?.board_id,
  boardId => loadSteps(boardId),
  {
    immediate: true,
  }
);
onMounted(loadBoards);

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}
function onBoardSelect(val) {
  update({
    board_id: val?.id ?? null,
    board_name: val?.name ?? null,
    step_id: null,
    step_name: null,
  });
}
function onStepSelect(val) {
  update({ step_id: val?.id ?? null, step_name: val?.name ?? null });
}
function onPrioritySelect(val) {
  update({ priority: val?.id ?? null });
}

// ─── Variable picker ──────────────────────────────────────────────────────────

const activeField = ref(null);
const varSearch = ref('');

const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(
    v => resolveLabel(v.label).toLowerCase().includes(q) || v.key.toLowerCase().includes(q)
  );
});

function openPicker(field) {
  activeField.value = field;
  varSearch.value = '';
}
function closePicker() {
  activeField.value = null;
  varSearch.value = '';
}
function selectVar(varKey, fieldKey) {
  const current = props.modelValue?.[fieldKey] ?? '';
  update({ [fieldKey]: current + `{{${varKey}}}` });
  closePicker();
}

// ─── Custom attributes ────────────────────────────────────────────────────────

const customAttrs = computed(() => props.modelValue?.custom_attributes ?? []);

function addCustomAttr() {
  update({ custom_attributes: [...customAttrs.value, { key: '', value: '' }] });
}
function removeCustomAttr(idx) {
  const updated = customAttrs.value.filter((_, i) => i !== idx);
  update({ custom_attributes: updated });
}
function updateCustomAttr(idx, field, val) {
  const updated = customAttrs.value.map((attr, i) =>
    i === idx ? { ...attr, [field]: val } : attr
  );
  update({ custom_attributes: updated });
}

// picker for custom attr values (uses `custom_attr_${idx}` as field id)
function selectVarForAttr(varKey, idx) {
  const current = customAttrs.value[idx]?.value ?? '';
  updateCustomAttr(idx, 'value', current + `{{${varKey}}}`);
  closePicker();
}

// ─── Simple text fields ───────────────────────────────────────────────────────

const TEXT_FIELDS = computed(() => [
  {
    key: 'title',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.TITLE'),
    placeholder: t(
      'FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.TITLE_PLACEHOLDER'
    ),
  },
  {
    key: 'due_date',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.DUE_DATE'),
    placeholder: t(
      'FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.DUE_DATE_PLACEHOLDER'
    ),
  },
  {
    key: 'start_date',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.START_DATE'),
    placeholder: t(
      'FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.START_DATE_PLACEHOLDER'
    ),
  },
  {
    key: 'value',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.VALUE'),
    placeholder: t(
      'FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.VALUE_PLACEHOLDER'
    ),
  },
  {
    key: 'ecommerce_order_id',
    label: t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.ORDER_ID'),
    placeholder: t(
      'FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.ORDER_ID_PLACEHOLDER'
    ),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Board -->
    <div class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{
        t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.BOARD')
      }}</span>
      <MultiselectDropdown
        :options="boardOptions"
        :selected-item="selectedBoard"
        :multiselector-placeholder="
          t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.SELECT_BOARD')
        "
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        @select="onBoardSelect"
      />
    </div>

    <!-- Step -->
    <div class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{
        t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.STEP')
      }}</span>
      <MultiselectDropdown
        :options="stepOptions"
        :selected-item="selectedStep"
        :multiselector-placeholder="
          loadingSteps
            ? t('FLOW_BUILDER.COMMON.LOADING')
            : t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.SELECT_STEP')
        "
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        @select="onStepSelect"
      />
    </div>

    <!-- Text fields with inline variable picker -->
    <div
      v-for="field in TEXT_FIELDS"
      :key="field.key"
      class="flex flex-col gap-1.5"
    >
      <span class="text-sm font-medium text-n-slate-12">{{ field.label }}</span>
      <div
        v-on-click-outside="() => activeField === field.key && closePicker()"
        class="relative"
      >
        <div
          class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all"
        >
          <input
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-sm !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
            :value="modelValue?.[field.key] ?? ''"
            :placeholder="field.placeholder"
            @input="update({ [field.key]: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="openPicker(field.key)"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="activeField === field.key"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div
              class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
            >
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                v-model="varSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                autofocus
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="selectVar(v.key, field.key)"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p
              v-if="!filteredVars.length"
              class="px-3 py-2 text-xs text-n-slate-9 italic"
            >
              {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Description (textarea + inline variable picker) -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{
        t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.DESCRIPTION')
      }}</span>
      <div
        v-on-click-outside="
          () => activeField === 'description' && closePicker()
        "
        class="relative"
      >
        <div class="relative">
          <textarea
            rows="3"
            class="reset-base w-full px-3 py-2 pr-9 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none transition-all"
            :value="modelValue?.description ?? ''"
            :placeholder="
              t(
                'FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.DESCRIPTION_PLACEHOLDER'
              )
            "
            @input="update({ description: $event.target.value })"
          />
          <button
            type="button"
            class="absolute top-2 right-2 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="openPicker('description')"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="activeField === 'description'"
          class="absolute top-0 right-0 mt-1 mr-8 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div
              class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
            >
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                v-model="varSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                autofocus
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="selectVar(v.key, 'description')"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p
              v-if="!filteredVars.length"
              class="px-3 py-2 text-xs text-n-slate-9 italic"
            >
              {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Priority -->
    <div class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{
        t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.PRIORITY')
      }}</span>
      <MultiselectDropdown
        :options="PRIORITY_OPTIONS"
        :selected-item="selectedPriority"
        :multiselector-placeholder="
          t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.SELECT_PRIORITY')
        "
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        @select="onPrioritySelect"
      />
    </div>

    <!-- Custom attributes -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">{{
        t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.CUSTOM_ATTRIBUTES')
      }}</span>
      <div
        v-for="(attr, idx) in customAttrs"
        :key="idx"
        class="flex items-start gap-1.5"
      >
        <!-- Key -->
        <input
          class="reset-base flex-1 min-w-0 h-9 px-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 transition-all"
          :value="attr.key"
          :placeholder="
            t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.ATTR_KEY_PLACEHOLDER')
          "
          @input="updateCustomAttr(idx, 'key', $event.target.value)"
        />
        <!-- Value with variable picker -->
        <div
          v-on-click-outside="
            () => activeField === `custom_attr_${idx}` && closePicker()
          "
          class="relative flex-1 min-w-0"
        >
          <div
            class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all"
          >
            <input
              class="reset-base flex-1 min-w-0 h-9 bg-transparent !text-sm !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
              :value="attr.value"
              :placeholder="
                t(
                  'FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.ATTR_VALUE_PLACEHOLDER'
                )
              "
              @input="updateCustomAttr(idx, 'value', $event.target.value)"
            />
            <button
              type="button"
              class="shrink-0 inline-flex items-center justify-center size-5 rounded text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
              :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
              @click.stop="openPicker(`custom_attr_${idx}`)"
            >
              <span class="i-ph-brackets-curly size-3 flex-shrink-0" />
            </button>
          </div>
          <div
            v-if="activeField === `custom_attr_${idx}`"
            class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
          >
            <div class="px-2 pt-2 pb-1">
              <div
                class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
              >
                <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                <input
                  v-model="varSearch"
                  type="text"
                  class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                  :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')"
                  autofocus
                />
              </div>
            </div>
            <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
              <button
                v-for="v in filteredVars"
                :key="v.key"
                type="button"
                class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="selectVarForAttr(v.key, idx)"
              >
                {{ resolveLabel(v.label) }}
              </button>
              <p
                v-if="!filteredVars.length"
                class="px-3 py-2 text-xs text-n-slate-9 italic"
              >
                {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
              </p>
            </div>
          </div>
        </div>
        <!-- Remove -->
        <button
          type="button"
          class="shrink-0 inline-flex items-center justify-center size-8 rounded-lg text-n-slate-9 hover:bg-n-ruby-3 hover:text-n-ruby-11 transition-colors"
          @click="removeCustomAttr(idx)"
        >
          <span class="i-lucide-trash-2 size-3.5" />
        </button>
      </div>
      <Button
        ghost
        slate
        sm
        icon="i-lucide-plus"
        :label="t('FLOW_BUILDER.ACTION_FORMS.CREATE_KANBAN_TASK.ADD_ATTRIBUTE')"
        class="self-start"
        @click="addCustomAttr"
      />
    </div>
  </div>
</template>
